const TENCENT_CAPTCHA_JS_URL =
  'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js'

const CAPTCHA_APP_ID_MAP = {
  prod: '189986908',
  uat: '189952302'
}

export type TencentCaptchaLang = 'zh' | 'en' | 'ms' | 'my'

export interface TencentCaptchaResponse {
  ret: number
  ticket?: string
  randstr?: string
  CaptchaAppId?: string
  bizState?: unknown
  errorCode?: number
  errorMessage?: string
}

export interface TencentCaptchaInstance {
  show: () => void
}

interface TencentCaptchaConstructor {
  new (
    container: HTMLElement,
    appId: string,
    callback: (res: TencentCaptchaResponse) => void,
    options: Record<string, unknown>
  ): TencentCaptchaInstance
}

declare global {
  interface Window {
    TencentCaptcha?: TencentCaptchaConstructor
  }
}

export const getTencentCaptchaAppId = (): string => {
  return import.meta.env.VITE_APP_ENV === 'prod'
    ? CAPTCHA_APP_ID_MAP.prod
    : CAPTCHA_APP_ID_MAP.uat
}

export const getTencentCaptchaLang = (langType?: string): string => {
  const normalizedLang = langType === 'my' ? 'ms' : langType
  const langMap: Record<string, string> = {
    zh: 'zh-cn',
    en: 'en',
    ms: 'ms'
  }

  return langMap[normalizedLang || ''] || 'en'
}

let sdkLoadPromise: Promise<void> | null = null

export const loadTencentCaptcha = (): Promise<void> => {
  if (window.TencentCaptcha) {
    return Promise.resolve()
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TENCENT_CAPTCHA_JS_URL
    script.async = true
    script.onload = () => {
      if (window.TencentCaptcha) {
        resolve()
        return
      }

      sdkLoadPromise = null
      script.remove()
      reject(new Error('TencentCaptcha SDK loaded without constructor'))
    }
    script.onerror = () => {
      sdkLoadPromise = null
      script.remove()
      reject(new Error('TencentCaptcha SDK load failed'))
    }
    document.body.appendChild(script)
  })

  return sdkLoadPromise
}

export const createTencentCaptcha = async (params: {
  container: HTMLElement
  callback: (res: TencentCaptchaResponse) => void
  langType?: TencentCaptchaLang | string
  appId?: string
  options?: Record<string, unknown>
}): Promise<TencentCaptchaInstance> => {
  await loadTencentCaptcha()

  if (!window.TencentCaptcha) {
    throw new Error('TencentCaptcha SDK not loaded')
  }

  return new window.TencentCaptcha(
    params.container,
    params.appId || getTencentCaptchaAppId(),
    params.callback,
    {
      ...params.options,
      userLanguage: getTencentCaptchaLang(params.langType)
    }
  )
}
